import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  GraduationCap, 
  Users, 
  Clock, 
  Star, 
  Calendar,
  MessageCircle,
  Search,
  RefreshCw,
  Bookmark,
  Hash,
  Play
} from 'lucide-react';

interface User {
  id: number;
  username: string;
  fullName?: string;
  bio?: string;
  profilePhotoUrl?: string;
  credits: number;
}

interface Skill {
  id: number;
  name: string;
  description?: string;
  category: {
    id: number;
    name: string;
  };
}

interface UserSkill {
  id: number;
  user: User;
  skill: Skill;
  relationType: 'TEACH' | 'LEARN';
  proficiencyLevel: number;
  proficiencyDescription?: string;
  hourlyRate?: number; // Credits per session
  availability?: string;
  totalSessions?: number;
  createdAt: string;
  updatedAt: string;
}

interface SessionRequestForm {
  proposedDateTime: string;
  durationMinutes: number;
  message: string;
}

type FeedTab = 'all' | 'teaching' | 'learning';



// Trending topics data - Comprehensive and diverse learning resources
const trendingTopics = [
  // Programming & Technology
  {
    id: 1,
    title: 'Complete Python Bootcamp: From Zero to Hero in 2024',
    author: 'Jose Portilla',
    timeAgo: '2 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400&h=200&fit=crop',
    category: 'Programming',
    url: 'https://www.udemy.com/course/complete-python-bootcamp/'
  },
  {
    id: 2,
    title: 'Machine Learning A-Z: Hands-On Python & R in Data Science',
    author: 'Kirill Eremenko',
    timeAgo: '5 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop',
    category: 'Data Science',
    url: 'https://www.udemy.com/course/machinelearning/'
  },
  {
    id: 3,
    title: 'The Complete JavaScript Course 2024: From Zero to Expert',
    author: 'Jonas Schmedtmann',
    timeAgo: '1 day ago',
    thumbnail: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=200&fit=crop',
    category: 'Web Development',
    url: 'https://www.udemy.com/course/the-complete-javascript-course/'
  },
  {
    id: 4,
    title: 'React - The Complete Guide (incl Hooks, React Router, Redux)',
    author: 'Maximilian Schwarzmüller',
    timeAgo: '3 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=200&fit=crop',
    category: 'Frontend Development',
    url: 'https://www.udemy.com/course/react-the-complete-guide-incl-redux/'
  },
  {
    id: 5,
    title: 'Node.js, Express, MongoDB & More: The Complete Bootcamp',
    author: 'Jonas Schmedtmann',
    timeAgo: '4 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=200&fit=crop',
    category: 'Backend Development',
    url: 'https://www.udemy.com/course/nodejs-express-mongodb-bootcamp/'
  },
  {
    id: 6,
    title: 'AWS Certified Solutions Architect - Associate 2024',
    author: 'Stephane Maarek',
    timeAgo: '6 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=200&fit=crop',
    category: 'Cloud Computing',
    url: 'https://www.udemy.com/course/aws-certified-solutions-architect-associate/'
  },
  {
    id: 7,
    title: 'Docker & Kubernetes: The Practical Guide',
    author: 'Maximilian Schwarzmüller',
    timeAgo: '1 week ago',
    thumbnail: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=400&h=200&fit=crop',
    category: 'DevOps',
    url: 'https://www.udemy.com/course/docker-kubernetes-the-practical-guide/'
  },
  {
    id: 8,
    title: 'Complete SQL Bootcamp 2024: Go from Zero to Hero',
    author: 'Jose Portilla',
    timeAgo: '2 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=400&h=200&fit=crop',
    category: 'Database',
    url: 'https://www.udemy.com/course/the-complete-sql-bootcamp/'
  },
  // Design & Creative
  {
    id: 9,
    title: 'UI/UX Design Bootcamp: Learn Design & Prototyping',
    author: 'Daniel Walter Scott',
    timeAgo: '3 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1561070791-2526d41294b5?w=400&h=200&fit=crop',
    category: 'Design',
    url: 'https://www.udemy.com/course/ui-ux-design-bootcamp/'
  },
  {
    id: 10,
    title: 'Adobe Photoshop CC - Essentials Training Course',
    author: 'Daniel Walter Scott',
    timeAgo: '5 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1626785774573-4b799315486d?w=400&h=200&fit=crop',
    category: 'Graphic Design',
    url: 'https://www.udemy.com/course/adobe-photoshop-cc-essentials-training-course/'
  },
  {
    id: 11,
    title: 'Complete Blender Creator: Learn 3D Modelling for Beginners',
    author: 'GameDev.tv Team',
    timeAgo: '1 week ago',
    thumbnail: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=200&fit=crop',
    category: '3D Modeling',
    url: 'https://www.udemy.com/course/blendertutorial/'
  },
  // Business & Marketing
  {
    id: 12,
    title: 'Digital Marketing Masterclass - 23 Courses in 1',
    author: 'Phil Ebiner',
    timeAgo: '4 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1557838923-2985c318be48?w=400&h=200&fit=crop',
    category: 'Marketing',
    url: 'https://www.udemy.com/course/digital-marketing-masterclass/'
  },
  {
    id: 13,
    title: 'Complete Financial Analyst Training 2024',
    author: 'Chris Haroun',
    timeAgo: '2 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=200&fit=crop',
    category: 'Finance',
    url: 'https://www.udemy.com/course/the-complete-financial-analyst-training-course/'
  },
  {
    id: 14,
    title: 'Business Strategy: Building Competitive Advantage',
    author: 'Michael Porter',
    timeAgo: '1 week ago',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop',
    category: 'Business Strategy',
    url: 'https://www.coursera.org/learn/competitive-strategy'
  },
  // Language Learning
  {
    id: 15,
    title: 'Complete Spanish Course: Learn Spanish for Beginners',
    author: 'Linguistic Learning',
    timeAgo: '3 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=400&h=200&fit=crop',
    category: 'Language Learning',
    url: 'https://www.udemy.com/course/complete-spanish-course/'
  },
  {
    id: 16,
    title: 'Japanese for Beginners: Learning Conversational Japanese',
    author: 'Japanese From Zero!',
    timeAgo: '5 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    category: 'Language Learning',
    url: 'https://www.udemy.com/course/japanese-for-beginners/'
  },
  // Science & Math
  {
    id: 17,
    title: 'Complete Mathematics for Computer Science',
    author: 'MIT OpenCourseWare',
    timeAgo: '1 week ago',
    thumbnail: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=200&fit=crop',
    category: 'Mathematics',
    url: 'https://ocw.mit.edu/courses/mathematics/18-01sc-single-variable-calculus-fall-2010/'
  },
  {
    id: 18,
    title: 'Physics for Scientists and Engineers',
    author: 'Stanford Online',
    timeAgo: '4 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop',
    category: 'Physics',
    url: 'https://online.stanford.edu/courses/soe-physics-physics-scientists-and-engineers'
  },
  // Health & Wellness
  {
    id: 19,
    title: 'Complete Fitness Trainer Certification: Beginner to Advanced',
    author: 'Fitness Academy',
    timeAgo: '2 days ago',
    thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    category: 'Fitness',
    url: 'https://www.udemy.com/course/complete-fitness-trainer-certification/'
  },
  {
    id: 20,
    title: 'Nutrition and Health: Macronutrients and Overnutrition',
    author: 'Wageningen University',
    timeAgo: '1 week ago',
    thumbnail: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=200&fit=crop',
    category: 'Nutrition',
    url: 'https://www.coursera.org/learn/nutrition'
  }
];

// Study resources - Comprehensive learning materials across various subjects
const studyResources = [
  // Programming & Computer Science
  {
    text: '#Free Python Programming Books & Tutorials',
    url: 'https://www.python.org/doc/'
  },
  {
    text: '#JavaScript Complete Reference Guide',
    url: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript'
  },
  {
    text: '#Java Programming Language Documentation',
    url: 'https://docs.oracle.com/javase/tutorial/'
  },
  {
    text: '#C++ Programming Tutorials & Examples',
    url: 'https://www.cplusplus.com/doc/tutorial/'
  },
  {
    text: '#Data Structures & Algorithms Visualization',
    url: 'https://visualgo.net/en'
  },
  {
    text: '#Git & GitHub Complete Guide',
    url: 'https://git-scm.com/doc'
  },
  {
    text: '#Linux Command Line Tutorial',
    url: 'https://www.learnshell.org/'
  },
  {
    text: '#SQL Database Tutorials & Practice',
    url: 'https://www.sqlbolt.com/'
  },
  // Mathematics & Statistics
  {
    text: '#Advanced Calculus Online Textbook',
    url: 'https://openstax.org/details/books/calculus-volume-1'
  },
  {
    text: '#Linear Algebra Interactive Course',
    url: 'https://www.khanacademy.org/math/linear-algebra'
  },
  {
    text: '#Probability & Statistics Fundamentals',
    url: 'https://www.statisticshowto.com/'
  },
  {
    text: '#Discrete Mathematics for Computer Science',
    url: 'https://discrete.openmathbooks.org/'
  },
  {
    text: '#Number Theory & Cryptography',
    url: 'https://www.math.uci.edu/~mathcirc/math184/184.html'
  },
  // Physics & Engineering
  {
    text: '#Classical Mechanics Online Course',
    url: 'https://www.physicsclassroom.com/'
  },
  {
    text: '#Electromagnetism Interactive Simulations',
    url: 'https://phet.colorado.edu/en/simulations/electromagnets'
  },
  {
    text: '#Quantum Physics for Beginners',
    url: 'https://quantumphysics.ucdavis.edu/'
  },
  {
    text: '#Engineering Mathematics Resources',
    url: 'https://www.engineeringtoolbox.com/'
  },
  {
    text: '#Circuit Analysis & Electronics',
    url: 'https://www.allaboutcircuits.com/'
  },
  // Chemistry & Biology
  {
    text: '#Organic Chemistry Interactive Textbook',
    url: 'https://www.organicchemistrytutor.com/'
  },
  {
    text: '#Biochemistry Online Course Materials',
    url: 'https://www.khanacademy.org/science/biology'
  },
  {
    text: '#Molecular Biology & Genetics',
    url: 'https://www.nature.com/scitable'
  },
  {
    text: '#Chemical Engineering Fundamentals',
    url: 'https://www.cheresources.com/'
  },
  // Business & Economics
  {
    text: '#Microeconomics Principles',
    url: 'https://www.khanacademy.org/economics-finance-domain/microeconomics'
  },
  {
    text: '#Financial Accounting Tutorials',
    url: 'https://www.accountingcoach.com/'
  },
  {
    text: '#Marketing Strategy & Consumer Behavior',
    url: 'https://www.marketingprofs.com/'
  },
  {
    text: '#Business Strategy & Management',
    url: 'https://hbr.org/'
  },
  // Language Learning
  {
    text: '#Spanish Grammar & Vocabulary',
    url: 'https://www.spanishdict.com/'
  },
  {
    text: '#French Language Learning Resources',
    url: 'https://www.laits.utexas.edu/tex/'
  },
  {
    text: '#German Grammar & Exercises',
    url: 'https://www.deutsch-lernen.com/'
  },
  {
    text: '#Japanese Language Study Materials',
    url: 'https://www.guidetojapanese.org/'
  },
  // Arts & Design
  {
    text: '#Digital Art & Design Principles',
    url: 'https://www.behance.net/'
  },
  {
    text: '#Photography Composition & Techniques',
    url: 'https://digital-photography-school.com/'
  },
  {
    text: '#Music Theory & Composition',
    url: 'https://www.musictheory.net/'
  },
  {
    text: '#Creative Writing & Storytelling',
    url: 'https://www.writersdigest.com/'
  },
  // Health & Medicine
  {
    text: '#Anatomy & Physiology Interactive',
    url: 'https://www.innerbody.com/'
  },
  {
    text: '#Medical Terminology & Definitions',
    url: 'https://www.medicinenet.com/'
  },
  {
    text: '#Nutrition Science & Diet Planning',
    url: 'https://www.nutrition.gov/'
  },
  {
    text: '#Psychology & Human Behavior',
    url: 'https://www.psychologytoday.com/'
  },
  // History & Social Sciences
  {
    text: '#World History Timeline & Events',
    url: 'https://www.history.com/'
  },
  {
    text: '#Philosophy & Critical Thinking',
    url: 'https://plato.stanford.edu/'
  },
  {
    text: '#Sociology & Social Research',
    url: 'https://www.asanet.org/'
  },
  {
    text: '#Political Science & International Relations',
    url: 'https://www.apsanet.org/'
  }
];

const Feed: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  
  // State for randomized content
  const [randomizedTrendingTopics, setRandomizedTrendingTopics] = useState<any[]>([]);
  const [randomizedStudyResources, setRandomizedStudyResources] = useState<any[]>([]);
  
  // Helper function to get proficiency level description
  const getProficiencyDescription = (level: number) => {
    switch (level) {
      case 5: return 'Expert - Master level with extensive experience';
      case 4: return 'Advanced - Highly skilled with deep knowledge';
      case 3: return 'Intermediate - Solid foundation with practical experience';
      case 2: return 'Beginner - Basic understanding, learning actively';
      case 1: return 'Novice - Just starting, eager to learn';
      default: return 'Level not specified';
    }
  };
  
  // Function to randomize study resources (show 8 random ones)
  const randomizeStudyResources = () => {
    const shuffled = [...studyResources].sort(() => 0.5 - Math.random());
    setRandomizedStudyResources(shuffled.slice(0, 8));
  };
  
  // Function to get random time ago
  const getRandomTimeAgo = () => {
    const timeOptions = [
      '1 hour ago', '2 hours ago', '3 hours ago', '4 hours ago', '5 hours ago',
      '6 hours ago', '12 hours ago', '1 day ago', '2 days ago', '3 days ago',
      '4 days ago', '5 days ago', '1 week ago', '2 weeks ago', '3 weeks ago'
    ];
    return timeOptions[Math.floor(Math.random() * timeOptions.length)];
  };
  
  // Function to randomize trending topics with fresh timestamps
  const randomizeTrendingTopics = () => {
    const shuffled = [...trendingTopics].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, 6);
    
    // Add random timestamps to make content appear fresh
    const withRandomTimes = selected.map(topic => ({
      ...topic,
      timeAgo: getRandomTimeAgo()
    }));
    
    setRandomizedTrendingTopics(withRandomTimes);
  };

  // Generate relevant image for skill posts based on skill name and category
  const getSkillImage = (skillName: string, categoryName: string) => {
    // Map skills to relevant Unsplash images
    const skillImageMap: { [key: string]: string } = {
      // Programming & Technology
      'Java Programming': 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=200&fit=crop',
      'Python Programming': 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400&h=200&fit=crop',
      'JavaScript': 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=200&fit=crop',
      'Web Development': 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=200&fit=crop',
      'Mobile Development': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=200&fit=crop',
      'Data Science': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop',
      'Machine Learning': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=200&fit=crop',
      'Artificial Intelligence': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=200&fit=crop',
      
      // Language Learning
      'Spanish': 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=400&h=200&fit=crop',
      'French': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=200&fit=crop',
      'German': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=200&fit=crop',
      'Chinese': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
      'Japanese': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
      
      // Mathematics & Science
      'Mathematics': 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=200&fit=crop',
      'Calculus': 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=200&fit=crop',
      'Physics': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop',
      'Chemistry': 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=200&fit=crop',
      'Biology': 'https://images.unsplash.com/photo-1530026405186-ed1f139313f1?w=400&h=200&fit=crop',
      
      // Arts & Creative
      'Photography': 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=400&h=200&fit=crop',
      'Drawing': 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=200&fit=crop',
      'Painting': 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=200&fit=crop',
      'Music': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=200&fit=crop',
      'Guitar': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=200&fit=crop',
      'Piano': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=200&fit=crop',
      
      // Business & Finance
      'Business': 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=200&fit=crop',
      'Marketing': 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=200&fit=crop',
      'Finance': 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=200&fit=crop',
      'Accounting': 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=200&fit=crop',
      
      // Health & Fitness
      'Fitness': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
      'Yoga': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=200&fit=crop',
      'Nutrition': 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=200&fit=crop',
      'Meditation': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=200&fit=crop',
      
      // Cooking & Food
      'Cooking': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=200&fit=crop',
      'Baking': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=200&fit=crop',
      'Culinary Arts': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=200&fit=crop',
      
      // Writing & Literature
      'Writing': 'https://images.unsplash.com/photo-1455390581842-9ac487cb8fa0?w=400&h=200&fit=crop',
      'Creative Writing': 'https://images.unsplash.com/photo-1455390581842-9ac487cb8fa0?w=400&h=200&fit=crop',
      'Poetry': 'https://images.unsplash.com/photo-1455390581842-9ac487cb8fa0?w=400&h=200&fit=crop',
      
      // Sports & Athletics
      'Soccer': 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&h=200&fit=crop',
      'Basketball': 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=200&fit=crop',
      'Tennis': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=400&h=200&fit=crop',
      'Swimming': 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=400&h=200&fit=crop'
    };

    // Try to find an exact match first
    if (skillImageMap[skillName]) {
      return skillImageMap[skillName];
    }

    // Try to find a partial match
    for (const [key, imageUrl] of Object.entries(skillImageMap)) {
      if (skillName.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(skillName.toLowerCase())) {
        return imageUrl;
      }
    }

    // Fallback to category-based images
    const categoryImageMap: { [key: string]: string } = {
      'Programming': 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=200&fit=crop',
      'Language Learning': 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=400&h=200&fit=crop',
      'Mathematics': 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=200&fit=crop',
      'Science': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop',
      'Arts': 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=200&fit=crop',
      'Music': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=200&fit=crop',
      'Business': 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=200&fit=crop',
      'Health': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
      'Cooking': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=200&fit=crop',
      'Sports': 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&h=200&fit=crop'
    };

    if (categoryImageMap[categoryName]) {
      return categoryImageMap[categoryName];
    }

    // Final fallback to a generic learning image
    return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=200&fit=crop';
  };

  // Add custom styles for scrollbar hiding
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    
    // Randomize content on component mount
    randomizeTrendingTopics();
    randomizeStudyResources();
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [userSkills, setUserSkills] = useState<UserSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FeedTab>('all');
  const [showSessionRequestModal, setShowSessionRequestModal] = useState(false);
  const [showTeacherMessageModal, setShowTeacherMessageModal] = useState(false);
  const [selectedUserSkill, setSelectedUserSkill] = useState<UserSkill | null>(null);
  const [teacherMessageForm, setTeacherMessageForm] = useState({
    message: ''
  });
  const [sessionRequestForm, setSessionRequestForm] = useState<SessionRequestForm>({
    proposedDateTime: '',
    durationMinutes: 60,
    message: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkillFilter, setSelectedSkillFilter] = useState<string>('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('');
  const [availableSkills, setAvailableSkills] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [previousSkillsCount, setPreviousSkillsCount] = useState(0);

  useEffect(() => {
    loadUserSkillsFeed();
  }, []);

  useEffect(() => {
    // Only show notification for actual new skills, not just count changes
    if (userSkills.length > previousSkillsCount && previousSkillsCount > 0) {
      const newSkillsCount = userSkills.length - previousSkillsCount;
      // Only notify if there are genuinely new skills (more than just a refresh)
      if (newSkillsCount > 0) {
        addNotification({
          type: 'info',
          title: 'New Skills Available',
          message: `${newSkillsCount} new skill${newSkillsCount > 1 ? 's' : ''} have been added to the feed!`,
          category: 'feed'
        });
      }
    }
    setPreviousSkillsCount(userSkills.length);
  }, [userSkills.length, previousSkillsCount, addNotification]);

  const loadUserSkillsFeed = async (showRefresh = false) => {
    try {
      setLoading(true);
      const response = await apiService.getAllUsersSkillsForFeed();
      setUserSkills(response.data);
      
      // Extract available skills and categories for filtering
      const skills = Array.from(new Set(response.data.map(us => us.skill.name))).sort();
      const categories = Array.from(new Set(response.data.map(us => us.skill.category.name))).sort();
      setAvailableSkills(skills);
      setAvailableCategories(categories);
      
      // Reset filters when refreshing
      if (showRefresh) {
        setSearchQuery('');
        setSelectedSkillFilter('');
        setSelectedCategoryFilter('');
        toast.success('Feed refreshed successfully!');
      }
    } catch (error: any) {
      toast.error('Failed to load feed');
      console.error('Error loading feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionRequest = (userSkill: UserSkill) => {
    setSelectedUserSkill(userSkill);
    setShowSessionRequestModal(true);
  };

  const handleTeacherMessage = (userSkill: UserSkill) => {
    setSelectedUserSkill(userSkill);
    setShowTeacherMessageModal(true);
  };

  const handleSaveFeed = async (userSkill: UserSkill) => {
    try {
      await apiService.saveFeed({ userSkillId: userSkill.id });
      toast.success('Post saved successfully!');
    } catch (error) {
      console.error('Error saving feed:', error);
      toast.error('Failed to save post');
    }
  };

  const submitSessionRequest = async () => {
    if (!selectedUserSkill || !currentUser) return;

    try {
      setIsSubmittingRequest(true);
      
      const requestData = {
        recipientId: selectedUserSkill.user.id,
        skillId: selectedUserSkill.skill.id,
        proposedDateTime: sessionRequestForm.proposedDateTime,
        durationMinutes: sessionRequestForm.durationMinutes,
        message: sessionRequestForm.message || '',
        teacherId: selectedUserSkill.relationType === 'TEACH' ? selectedUserSkill.user.id : currentUser.id,
        learnerId: selectedUserSkill.relationType === 'TEACH' ? currentUser.id : selectedUserSkill.user.id
      };

      await apiService.createSessionRequest(requestData);
      
      toast.success('Session request sent successfully!');
      setShowSessionRequestModal(false);
      setSessionRequestForm({
        proposedDateTime: '',
        durationMinutes: 60,
        message: ''
      });
      
      // Refresh the feed to show updated data
      loadUserSkillsFeed();
    } catch (error: any) {
      console.error('Error submitting session request:', error);
      toast.error(error.response?.data?.message || 'Failed to send session request');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const submitTeacherMessage = async () => {
    if (!selectedUserSkill || !currentUser) return;

    try {
      setIsSubmittingRequest(true);
      
      // Get or create chat room
      const chatRoomResponse = await apiService.getOrCreateChatRoom(
        selectedUserSkill.user.id,
        selectedUserSkill.skill.id
      );
      
      // Send message
      await apiService.sendMessage({
        roomId: chatRoomResponse.data.id,
        content: teacherMessageForm.message,
        messageType: 'TEACHER_INTEREST',
        skillId: selectedUserSkill.skill.id
      });
      
      // Dispatch custom event for chat notification
      window.dispatchEvent(new CustomEvent('newChatNotification'));
      
      toast.success('Message sent successfully! You can continue the conversation in the chat tab.');
      setShowTeacherMessageModal(false);
      setTeacherMessageForm({
        message: ''
      });
      
      // Navigate to chat after a short delay
      setTimeout(() => {
        navigate('/chat');
      }, 1500);
    } catch (error: any) {
      console.error('Error sending teacher message:', error);
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Filter skills based on active tab and search query
  const filteredSkills = userSkills.filter((userSkill) => {
    // Tab filtering
    const matchesTab = 
      (activeTab === 'all') ||
      (activeTab === 'teaching' && userSkill.relationType === 'TEACH') ||
      (activeTab === 'learning' && userSkill.relationType === 'LEARN');

    // Search filtering
    const matchesSearch = searchQuery === '' ||
      userSkill.user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userSkill.user.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userSkill.skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userSkill.skill.category.name.toLowerCase().includes(searchQuery.toLowerCase());

    // Skill filtering
    const matchesSkill = selectedSkillFilter === '' || 
      userSkill.skill.name === selectedSkillFilter;

    // Category filtering
    const matchesCategory = selectedCategoryFilter === '' || 
      userSkill.skill.category.name === selectedCategoryFilter;

    return matchesTab && matchesSearch && matchesSkill && matchesCategory;
  });

  // Get counts for each tab
  const teachingCount = userSkills.filter(skill => skill.relationType === 'TEACH').length;
  const learningCount = userSkills.filter(skill => skill.relationType === 'LEARN').length;
  const allCount = userSkills.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-6 pulse-glow"></div>
          <h2 className="text-2xl font-bold gradient-text mb-2">LearnLoop</h2>
          <p className="text-gray-600 text-lg">Loading amazing skills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      {/* Modern Header */}
      <div className="bg-white shadow-lg border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Logo and Search */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 group">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transform group-hover:scale-110 transition-all duration-300">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold gradient-text group-hover:scale-105 transition-transform duration-300">LearnLoop</h1>
              </div>
              
              {/* Search Bar */}
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search here..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-80 pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white focus:scale-105 transition-all duration-300 shadow-sm hover:shadow-md"
                />
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors duration-300" />
              </div>
            </div>

            {/* Right side - Refresh Button */}
            <div className="flex items-center">
              <button 
                onClick={() => loadUserSkillsFeed(true)}
                disabled={loading}
                className="p-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                title="Refresh Feed"
              >
                <RefreshCw className={`h-6 w-6 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Filter Options */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-6 border-t border-blue-100 bg-gradient-to-r from-blue-50/50 to-teal-50/50"
          >
            {/* Left side - Filter Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Hash className="h-4 w-4 text-blue-600" />
                  Filter by:
                  {loading && (
                    <div className="h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </span>
                
                {/* Skill Filter */}
                <div className="relative group">
                  <select
                    value={selectedSkillFilter}
                    onChange={(e) => setSelectedSkillFilter(e.target.value)}
                    className="pl-4 pr-10 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 hover:shadow-md shadow-sm min-w-[140px]"
                  >
                    <option value="">All Skills</option>
                    {availableSkills.map((skill) => (
                      <option key={skill} value={skill}>
                        {skill}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="h-4 w-4 border-l border-gray-200 pl-2">
                      <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Category Filter */}
                <div className="relative group">
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    className="pl-4 pr-10 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 hover:shadow-md shadow-sm min-w-[160px]"
                  >
                    <option value="">All Categories</option>
                    {availableCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="h-4 w-4 border-l border-gray-200 pl-2">
                      <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clear Filters Button */}
              {(selectedSkillFilter || selectedCategoryFilter) && (
                <button
                  onClick={() => {
                    setSelectedSkillFilter('');
                    setSelectedCategoryFilter('');
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-300 flex items-center gap-2 border border-gray-200 hover:border-gray-300 hover:shadow-sm"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Filters
                </button>
              )}
            </div>

            {/* Right side - Active Filters Display */}
            {(selectedSkillFilter || selectedCategoryFilter) && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse"></div>
                  Active filters:
                </span>
                {selectedSkillFilter && (
                  <motion.span 
                    whileHover={{ scale: 1.05 }}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full border border-blue-200 shadow-sm cursor-pointer hover:bg-blue-200 hover:shadow-md transition-all duration-200"
                  >
                    <Hash className="h-3 w-3 inline mr-1" />
                    {selectedSkillFilter}
                  </motion.span>
                )}
                {selectedCategoryFilter && (
                  <motion.span 
                    whileHover={{ scale: 1.05 }}
                    className="px-3 py-1.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full border border-teal-200 shadow-sm cursor-pointer hover:bg-teal-200 hover:shadow-md transition-all duration-200"
                  >
                    <BookOpen className="h-3 w-3 inline mr-1" />
                    {selectedCategoryFilter}
                  </motion.span>
                )}
                <span className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full border border-gray-200">
                  {filteredSkills.length} posts visible
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg p-2 border border-blue-100 backdrop-blur-sm bg-white/80">
            <nav className="flex space-x-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 flex items-center justify-center py-3 px-6 rounded-lg font-medium text-sm transition-all duration-300 hover:scale-[1.02] ${
                  activeTab === 'all'
                    ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Users className="w-4 h-4 mr-2" />
                All Skills
                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                  activeTab === 'all' ? 'bg-white bg-opacity-20 text-white' : 'bg-blue-100 text-blue-600'
                }`}>
                  {allCount}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('teaching')}
                className={`flex-1 flex items-center justify-center py-3 px-6 rounded-lg font-medium text-sm transition-all duration-300 hover:scale-[1.02] ${
                  activeTab === 'teaching'
                    ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Teaching
                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                  activeTab === 'teaching' ? 'bg-white bg-opacity-20 text-white' : 'bg-teal-100 text-teal-600'
                }`}>
                  {teachingCount}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('learning')}
                className={`flex-1 flex items-center justify-center py-3 px-6 rounded-lg font-medium text-sm transition-all duration-300 hover:scale-[1.02] ${
                  activeTab === 'learning'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Learning
                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                  activeTab === 'learning' ? 'bg-white bg-opacity-20 text-white' : 'bg-blue-100 text-blue-600'
                }`}>
                  {learningCount}
                </span>
              </button>
            </nav>
          </div>
        </div>

        {/* Feed Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-6">
            {filteredSkills.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto h-20 w-20 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mb-6 shadow-lg">
                  <BookOpen className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold gradient-text mb-3">
                  {searchQuery ? 'No skills match your search' : 'No skills found'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery 
                    ? `Try adjusting your search terms or browse all categories`
                    : 'Get started by adding some skills to your profile or browse the feed'
                  }
                </p>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedSkillFilter('');
                      setSelectedCategoryFilter('');
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              filteredSkills.map((userSkill) => (
                <motion.div
                  key={userSkill.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ y: -2 }}
                  className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden hover:shadow-2xl hover:border-blue-200 transition-all duration-500 transform hover:scale-[1.01]"
                >
                                      {/* Skill Header */}
                    <div className="p-6 border-b border-blue-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 overflow-hidden">
                            {userSkill.user.profilePhotoUrl ? (
                              <img
                                src={userSkill.user.profilePhotoUrl}
                                alt={`${userSkill.user.fullName || userSkill.user.username}'s profile`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Hide the broken image and show fallback
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            {/* Fallback icon - hidden if image loads successfully */}
                            <span className={`text-white font-semibold text-lg ${userSkill.user.profilePhotoUrl ? 'hidden' : ''}`}>
                              {userSkill.user.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {userSkill.user.fullName || userSkill.user.username}
                            </h3>
                            <p className="text-sm text-gray-600 mb-1">
                              {userSkill.skill.name} • {userSkill.relationType === 'TEACH' ? 'Teaching' : 'Learning'}
                            </p>
                            <p className="text-xs text-gray-500">
                              @{userSkill.user.username} • {userSkill.user.credits} credits available
                            </p>
                            {userSkill.user.bio && (
                              <p className="text-sm text-gray-600 mt-2 italic">
                                "{userSkill.user.bio}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            userSkill.relationType === 'TEACH' 
                              ? 'bg-teal-100 text-teal-800 border border-teal-200' 
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`}>
                            {userSkill.relationType === 'TEACH' ? 'Teacher' : 'Learner'}
                          </span>
                        </div>
                      </div>
                    </div>

                  {/* Random Skill Image */}
                  <div className="relative overflow-hidden">
                    <img
                      src={getSkillImage(userSkill.skill.name, userSkill.skill.category.name)}
                      alt={`${userSkill.skill.name} - ${userSkill.skill.category.name}`}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    <div className="absolute bottom-3 left-3">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-800 rounded-full text-xs font-medium shadow-sm">
                        {userSkill.skill.category.name}
                      </span>
                    </div>
                  </div>

                  {/* Skill Details */}
                  <div className="p-6">
                    {/* Skill Information */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        {userSkill.skill.name}
                      </h4>
                      {userSkill.skill.description && (
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {userSkill.skill.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-teal-100 text-blue-800 rounded-full text-xs font-medium border border-blue-200">
                          {userSkill.skill.category.name}
                        </span>
                      </div>
                    </div>

                    {/* Proficiency and Experience */}
                    <div className={`grid gap-4 mb-6 ${
                      userSkill.totalSessions !== undefined && userSkill.totalSessions !== null && userSkill.totalSessions > 0 
                        ? 'grid-cols-1 md:grid-cols-2' 
                        : 'grid-cols-1'
                    }`}>
                      <div className="flex items-center space-x-2 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 hover:shadow-md transition-all duration-300">
                        <Star className="h-5 w-5 text-yellow-600" />
                        <div>
                          <span className="text-sm font-medium text-yellow-800">
                            Level {userSkill.proficiencyLevel}/5
                          </span>
                          <p className="text-xs text-yellow-700">
                            {getProficiencyDescription(userSkill.proficiencyLevel)}
                          </p>
                        </div>
                      </div>
                      
                      {userSkill.totalSessions !== undefined && userSkill.totalSessions !== null && userSkill.totalSessions > 0 && (
                        <div className="flex items-center space-x-2 p-3 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 hover:shadow-md transition-all duration-300">
                          <Users className="h-5 w-5 text-teal-600" />
                          <div>
                            <span className="text-sm font-medium text-teal-800">
                              {userSkill.totalSessions} sessions
                            </span>
                            <p className="text-xs text-teal-700">
                              {userSkill.relationType === 'TEACH' ? 'taught' : 'attended'}
                            </p>
                          </div>
                        </div>
                      )}
                      

                    </div>

                    {/* Proficiency Description */}
                    {userSkill.proficiencyDescription && userSkill.proficiencyDescription.trim() !== '' && (
                      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg border border-blue-200 hover:shadow-md transition-all duration-300">
                        <div className="flex items-start space-x-2">
                          <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h5 className="text-sm font-medium text-blue-900 mb-1">Experience & Background</h5>
                            <p className="text-sm text-blue-800 leading-relaxed">
                              {userSkill.proficiencyDescription}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Teaching-specific details */}
                    {userSkill.relationType === 'TEACH' && (
                      <div className="mb-6 space-y-3 relative z-10">
                        {userSkill.hourlyRate !== undefined && userSkill.hourlyRate !== null && userSkill.hourlyRate > 0 && (
                          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg border border-blue-200 hover:shadow-md transition-all duration-300">
                            <div className="flex items-center space-x-2">
                              <Clock className="h-5 w-5 text-blue-600" />
                              <span className="text-sm font-medium text-blue-900">
                                {userSkill.hourlyRate} Credits per session
                              </span>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-blue-700">Session Pricing</p>
                            </div>
                          </div>
                        )}
                        
                        {userSkill.availability && userSkill.availability.trim() !== '' && (
                          <div className="flex items-center space-x-2 p-3 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200 hover:shadow-md transition-all duration-300">
                            <Calendar className="h-5 w-5 text-teal-600 flex-shrink-0" />
                            <div>
                              <span className="text-sm font-medium text-teal-800">
                                Available: {userSkill.availability}
                              </span>
                              <p className="text-xs text-teal-700">Teaching Schedule</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                                      {/* Action Buttons - Only show for other users' posts */}
                  {currentUser && currentUser.id !== userSkill.user.id && (
                    <div className="flex space-x-3">
                      <button
                        onClick={() => userSkill.relationType === 'TEACH' ? handleSessionRequest(userSkill) : handleTeacherMessage(userSkill)}
                        className={`flex-1 py-3.5 px-6 rounded-xl font-semibold transition-all duration-300 relative z-10 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl ${
                          userSkill.relationType === 'TEACH'
                            ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700 focus:ring-4 focus:ring-blue-200'
                            : 'bg-gradient-to-r from-teal-600 to-blue-600 text-white hover:from-teal-700 hover:to-blue-700 focus:ring-4 focus:ring-teal-200'
                        }`}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <MessageCircle className="w-4 h-4" />
                          <span>
                            {userSkill.relationType === 'TEACH'
                              ? 'Request Learning Session'
                              : 'Message'
                            }
                          </span>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => handleSaveFeed(userSkill)}
                        className="py-3.5 px-4 rounded-xl font-semibold transition-all duration-300 relative z-10 transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 focus:ring-4 focus:ring-yellow-200 shadow-lg hover:shadow-xl"
                        title="Save this post"
                      >
                        <Bookmark className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                    {/* Timestamp */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          Added {new Date(userSkill.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        {userSkill.updatedAt !== userSkill.createdAt && (
                          <span>
                            Updated {new Date(userSkill.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Refresh All Button */}
            <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Learning Resources</h3>
                  <p className="text-blue-100 text-sm">Discover new topics and study materials</p>
                </div>
                <button
                  onClick={() => {
                    randomizeTrendingTopics();
                    randomizeStudyResources();
                    toast.success('Refreshed learning resources!');
                  }}
                  className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all duration-200 hover:scale-110"
                  title="Refresh all learning resources"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Trending Topics */}
            <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Trending Topics</h3>
              <div className="space-y-4">
                {randomizedTrendingTopics.map((topic) => (
                  <a
                    key={topic.id}
                    href={topic.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex space-x-3 group cursor-pointer p-2 rounded-lg hover:bg-blue-50 transition-all duration-200 block hover:scale-[1.02]"
                  >
                    <div className="flex-shrink-0 relative">
                      <img
                        src={topic.thumbnail}
                        alt={topic.title}
                        className="h-16 w-24 rounded-lg object-cover group-hover:scale-105 transition-transform duration-200 shadow-sm"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-20 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Play className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                        {topic.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {topic.author} • {topic.timeAgo}
                      </p>
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-gradient-to-r from-blue-100 to-teal-100 text-blue-800 rounded-full font-medium border border-blue-200">
                        {topic.category}
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Study Resources</h3>
              <div className="space-y-2">
                {randomizedStudyResources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-blue-600 cursor-pointer transition-all duration-200 group p-2 rounded-lg hover:bg-blue-50 hover:scale-[1.02]"
                  >
                    <Hash className="h-4 w-4 text-blue-500 group-hover:scale-110 transition-transform flex-shrink-0" />
                    <span className="font-medium">{resource.text}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Request Modal */}
      {showSessionRequestModal && selectedUserSkill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            {/* Loading Overlay */}
            {isSubmittingRequest && (
              <div className="absolute inset-0 bg-white bg-opacity-90 rounded-2xl flex items-center justify-center z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Sending session request...</p>
                  <p className="text-sm text-gray-500">Please wait while we process your request</p>
                </div>
              </div>
            )}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg overflow-hidden">
                {selectedUserSkill.user.profilePhotoUrl ? (
                  <img
                    src={selectedUserSkill.user.profilePhotoUrl}
                    alt={`${selectedUserSkill.user.fullName || selectedUserSkill.user.username}'s profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide the broken image and show fallback
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                {/* Fallback icon - hidden if image loads successfully */}
                <MessageCircle className={`w-8 h-8 text-white ${selectedUserSkill.user.profilePhotoUrl ? 'hidden' : ''}`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Request Session with {selectedUserSkill.user.fullName || selectedUserSkill.user.username}
              </h3>
              <p className="text-gray-600 mt-2">Let's start learning together!</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Proposed Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={sessionRequestForm.proposedDateTime}
                  onChange={(e) => setSessionRequestForm(prev => ({ ...prev, proposedDateTime: e.target.value }))}
                  disabled={isSubmittingRequest}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Duration
                </label>
                <select
                  value={sessionRequestForm.durationMinutes}
                  onChange={(e) => setSessionRequestForm(prev => ({ ...prev, durationMinutes: parseInt(e.target.value) }))}
                  disabled={isSubmittingRequest}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MessageCircle className="w-4 h-4 inline mr-2" />
                  Message
                </label>
                <textarea
                  value={sessionRequestForm.message}
                  onChange={(e) => setSessionRequestForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={3}
                  disabled={isSubmittingRequest}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Tell them what you'd like to learn or discuss..."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-8">
              <button
                onClick={() => setShowSessionRequestModal(false)}
                disabled={isSubmittingRequest}
                className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={submitSessionRequest}
                disabled={!sessionRequestForm.proposedDateTime || !sessionRequestForm.message || isSubmittingRequest}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
              >
                {isSubmittingRequest ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teacher Message Modal */}
      {showTeacherMessageModal && selectedUserSkill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            {/* Loading Overlay */}
            {isSubmittingRequest && (
              <div className="absolute inset-0 bg-white bg-opacity-90 rounded-2xl flex items-center justify-center z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Sending message...</p>
                  <p className="text-sm text-gray-500">Please wait while we process your message</p>
                </div>
              </div>
            )}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg overflow-hidden">
                {selectedUserSkill.user.profilePhotoUrl ? (
                  <img
                    src={selectedUserSkill.user.profilePhotoUrl}
                    alt={`${selectedUserSkill.user.fullName || selectedUserSkill.user.username}'s profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide the broken image and show fallback
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                {/* Fallback icon - hidden if image loads successfully */}
                <MessageCircle className={`w-8 h-8 text-white ${selectedUserSkill.user.profilePhotoUrl ? 'hidden' : ''}`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Message {selectedUserSkill.user.fullName || selectedUserSkill.user.username}
              </h3>
              <p className="text-gray-600 mt-2">
                Express your interest in teaching <span className="font-semibold">{selectedUserSkill.skill.name}</span>!
              </p>
              <p className="text-sm text-gray-500 mt-1">
                This will create a chat conversation where you can discuss teaching details.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MessageCircle className="w-4 h-4 inline mr-2" />
                  Message
                </label>
                <textarea
                  value={teacherMessageForm.message}
                  onChange={(e) => setTeacherMessageForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={4}
                  disabled={isSubmittingRequest}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Tell them why you'd be a great teacher for this skill, your experience, and what you can offer..."
                />
              </div>


              <p className="text-xs text-gray-500 italic">
                Your message will be sent to the learner and they can respond in the chat tab.
              </p>
            </div>

            <div className="flex space-x-3 mt-8">
              <button
                onClick={() => setShowTeacherMessageModal(false)}
                disabled={isSubmittingRequest}
                className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={submitTeacherMessage}
                disabled={!teacherMessageForm.message.trim() || isSubmittingRequest}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-xl hover:from-teal-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
              >
                {isSubmittingRequest ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending Message...</span>
                  </div>
                ) : (
                  'Send Message'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;


